/**
 * Connections settings tab (step 9a + recipient/initiator loop).
 *
 * Three logical sections, each a separate query:
 *   1. Incoming share requests   — GET /incoming  (recipient picker inbox)
 *   2. Your pending requests     — GET /outgoing  (initiator outbox)
 *   3. Connected organizations   — GET /          (established)
 *
 * The recipient/initiator loop is wired so a single user with multiple
 * tenants can drive the full protocol end-to-end:
 *   • Alice's tenant initiates → request shows in Awaiting (her outbox)
 *     and in Incoming (her recipient inbox, if same email)
 *   • She picks Personal → ESTABLISHED immediately; or
 *     picks the same org → pending_initiator_confirm → Confirm → ESTABLISHED
 *
 * The reverse-share / Share dialog (9d) will reuse the same client.
 */
import { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  AlertTriangle,
  Building2,
  Check,
  Loader2,
  Mail,
  Network,
  Plus,
  RefreshCw,
  Send,
  Trash2,
  User,
  X,
} from 'lucide-react'
import { useConfirm, useToast, useTheme, getThemeClass } from '@papan/core-ui/ui'
import { useRealtime } from '@papan/realtime/client'
import {
  orgConnectionsApi,
  type ConnectionRow,
  type OutgoingRequest,
} from '../client/index.js'
import { ConnectionForm, type ConnectionFormValues } from './ConnectionForm.js'
import { connectWarnings, connectWarningText } from './connect-warnings.js'

interface TenantOption {
  id: string
  name: string
  type: string  // 'personal' | 'organization'
}

interface Props {
  /** Caller's tenant id — used as the implicit sourceFolderId for
   *  the initiate call AND as the default accept target for
   *  incoming requests in org context. */
  tenantId: string
  /** Active tenant's personal-ness:
   *  - Personal tab → "Accept as Personal" button (no dropdown,
   *    Personal is the only target this tab manages).
   *  - Org tab → custom dropdown of all the user's orgs (lets the
   *    user re-route the accept to a different org without leaving
   *    Settings; current org is the default). */
  isPersonal?: boolean
  /** Every tenant the user is a member of. Org tabs' picker
   *  filters to type='organization' rows. Personal tab ignores
   *  this prop. */
  myTenants?: TenantOption[]
}

// Props are accepted for compatibility with the Settings host but no
// longer read: members accept/confirm from notifications, and the page
// fetches everything via the active-tenant-scoped API.
export function ConnectionsPage(_props: Props) {
  const toast = useToast()
  const confirm = useConfirm()
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const [rows, setRows] = useState<ConnectionRow[] | null>(null)
  const [outgoing, setOutgoing] = useState<OutgoingRequest[]>([])
  /** Notify-admin policy + whether the caller may edit it (owner/admin of an
   *  org tenant). null until loaded. */
  const [connSettings, setConnSettings] = useState<{ notifyAdmin: boolean; canManage: boolean } | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  /** #359: the "send another?" question for the request being submitted, answered in the add modal. */
  const [sendWarning, setSendWarning] = useState<{ text: string; resolve: (go: boolean) => void } | null>(null)
  const [editingContactFor, setEditingContactFor] = useState<string | null>(null)
  const [contactDraft, setContactDraft] = useState('')
  // Locally-dismissed "Completed · already connected" notices (per device).
  const [dismissedOutgoing, setDismissedOutgoing] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem('papan:dismissedOutgoing') || '[]') as string[]) }
    catch { return new Set() }
  })
  const dismissOutgoing = useCallback((id: string) => {
    setDismissedOutgoing(prev => {
      const next = new Set(prev); next.add(id)
      try { localStorage.setItem('papan:dismissedOutgoing', JSON.stringify([...next])) } catch { /* ignore */ }
      return next
    })
  }, [])

  const load = useCallback(async () => {
    setLoadError(null)
    try {
      const [a, c, e] = await Promise.all([
        orgConnectionsApi.list(),
        orgConnectionsApi.listOutgoing(),
        orgConnectionsApi.getSettings(),
      ])
      setRows(a.connections)
      setOutgoing(c.outgoing)
      setConnSettings(e)
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load')
    }
  }, [])

  useEffect(() => { void load() }, [load])

  // Live refresh: the connection lifecycle pushes notifications to both
  // sides (connection_established, *_awaiting_confirm, *_awaiting_approval,
  // share_request_received). When one lands, reload so a connection that
  // just established/changed shows up without a manual refresh.
  const { onNotification } = useRealtime()
  useEffect(() => {
    return onNotification((n) => {
      // share_request_closed (#1213): an outbox request just closed (its recipient was deleted) — show it resolved.
      if (n.type.startsWith('connection_') || n.type === 'share_request_received' || n.type === 'share_request_closed') {
        void load()
      }
    })
  }, [onNotification, load])

  // ─── Initiate ───────────────────────────────────────────────────────
  // Connection-only initiate: no sourceFolderId. The recipient's accept
  // creates ONLY the org_connections row — no _shared/<SourceOrg>/ tree
  // grows in their drive, no .shared artifact materializes. Specific
  // content shares happen later via the Share dialog (which carries a
  // real sourceFolderId) and silent-re-share over the existing connection.
  const handleSubmit = async (values: ConnectionFormValues) => {
    // #359: the server sends a second request to someone already connected (their
    // OTHER org) and a repeat while one is unanswered — so warn here, from this
    // org's own lists, and let the sender go ahead.
    // Asked INSIDE the add modal: it is portaled to <body>, so the app's confirm
    // (rendered in the app tree) would sit underneath it.
    const warning = connectWarningText(values.email, connectWarnings(values.email, rows ?? [], outgoing))
    if (warning) {
      const go = await new Promise<boolean>(resolve => setSendWarning({ text: warning, resolve }))
      setSendWarning(null)
      if (!go) return
    }
    const r = await orgConnectionsApi.initiate({
      recipientEmail: values.email,
      sourceFolderId: null,
      note: 'Connection request',
      setAsDefaultContact: values.setAsDefaultContact,
      organizationName: values.isOrganization ? values.organizationName : null,
      // "a Person". Since #359 a connection-only request reads nothing from it on
      // the server (the recipient picks the org; memberships are never consulted);
      // the share paths still use it, so the field stays the form's answer.
      recipientIsPersonal: !values.isOrganization,
    })
    // Network tab only sends connection-only initiates (sourceFolderId
    // is null), so the silent-reshare fast-path doesn't fire here.
    // Defensive branch in case that changes — wording matches the
    // ShareDialog's silent-reshare toast.
    if (r.silentReshare) {
      toast.success(`Already connected to ${values.email}`)
    } else {
      toast.success(`Connection request sent (${r.requestId.slice(0, 8)}…)`)
    }
    setAdding(false)
    void load()
  }

  // Closing the modal any way answers an open "send another?" with no.
  const closeAdd = () => { sendWarning?.resolve(false); setAdding(false) }

  // ─── Default contact ────────────────────────────────────────────────
  const handleSaveContact = async (connectionId: string) => {
    try {
      await orgConnectionsApi.setDefaultContact(
        connectionId,
        contactDraft.trim() || null,
      )
      toast.success('Default contact updated')
      setEditingContactFor(null)
      void load()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save')
    }
  }

  // ─── Connection policy toggle (owner/admin) ─────────────────────────
  const handleToggleNotify = async (value: boolean) => {
    setConnSettings(prev => prev ? { ...prev, notifyAdmin: value } : prev)
    try {
      await orgConnectionsApi.setNotifyAdmin(value)
      toast.success(value
        ? 'Admins will be notified of new connections'
        : 'Admins will no longer be notified of new connections')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update setting')
      void load()
    }
  }

  // (Accept and confirm now happen from notification popups, not here.)

  // ─── Revoke (disconnect an established connection) ──────────────────
  const handleDisconnect = async (row: ConnectionRow) => {
    const ok = await confirm({
      title: row.org.type === 'personal' ? 'Disconnect from this connection?' : 'Disconnect from this organization?',
      message: (
        <span>
          Disconnect from <strong>{row.org.name}</strong>? Any in-flight
          share requests with them will be cancelled. Existing materialized
          shares (the <code>.shared</code> files) are NOT deleted — they
          remain in your tree until you remove them explicitly. You can
          reconnect later by sharing again.
        </span>
      ),
      type: 'danger',
      confirmText: 'Disconnect',
    })
    if (!ok) return
    try {
      await orgConnectionsApi.revoke(row.connectionId)
      toast.success(`Disconnected from ${row.org.name}`)
      void load()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to disconnect')
    }
  }

  // ─── Reject (initiator) ─────────────────────────────────────────────
  const handleCancel = async (req: OutgoingRequest) => {
    try {
      await orgConnectionsApi.reject(req.requestId)
      toast.success('Request cancelled')
      void load()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to cancel')
    }
  }

  const visibleOutgoing = outgoing.filter(r => !dismissedOutgoing.has(r.requestId))

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg themed-text font-medium flex items-center gap-2">
            <Network size={18} className="themed-icon" />
            Network
          </h2>
          <p className="text-xs themed-text-muted mt-1">
            People and organizations you're connected to. Each connection enables silent re-share for future content.
          </p>
        </div>
        <button
          onClick={() => void load()}
          className="text-xs themed-text-muted hover:themed-text flex items-center gap-1 transition-colors"
          title="Refresh"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {loadError && (
        <p className="text-xs text-red-400">{loadError}</p>
      )}

      {/* ─── Connection policy (owner/admin of an org tenant) ─────── */}
      {connSettings?.canManage && (
        <div className="flex items-start justify-between gap-3 p-3 rounded-md border themed-border">
          <div className="flex-1 min-w-0">
            <span className="text-sm themed-text">Notify admins of new connections</span>
            <span className="block text-xs themed-text-muted mt-0.5">
              When on, owners and admins get a notification whenever a member
              connects this organization to another. It doesn't block anything.
            </span>
          </div>
          <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-0.5">
            <input
              type="checkbox"
              checked={connSettings.notifyAdmin}
              onChange={(e) => void handleToggleNotify(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-zinc-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-zinc-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
      )}

      {/* Incoming requests are accepted from the notification bell now
          (click the request → Connect popup), not from this tab. */}

      {/* ─── Outgoing — your pending requests ─────────────────────── */}
      {visibleOutgoing.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-sm themed-text font-medium flex items-center gap-2">
            <Send size={14} className="themed-icon" />
            Your pending requests
            <span className="text-xs themed-text-muted font-normal">({visibleOutgoing.length})</span>
          </h3>
          <ul className="space-y-2">
            {visibleOutgoing.map(req => {
              const needsConfirm = req.state === 'pending_initiator_confirm'
              // Auto-completed because the two orgs got connected by another request, or closed because the
              // organization (or account) it was addressed to was deleted (#1213) — shown as resolved (with Dismiss)
              // rather than a live, cancellable request.
              if (req.completed) {
                return (
                  <ResolvedRequestCard
                    key={req.requestId}
                    req={req}
                    onDismiss={() => dismissOutgoing(req.requestId)}
                  />
                )
              }
              return (
                <li
                  key={req.requestId}
                  className={`p-3 rounded-md border themed-border ${needsConfirm ? 'border-blue-500/40' : ''}`}
                >
                  <div className="flex items-start gap-3">
                    <Send size={16} className="themed-icon mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm themed-text">
                        To <span className="font-mono">{req.recipientEmail}</span>
                      </p>
                      <p className="text-[11px] themed-text-muted mt-0.5 truncate" title={req.initiatorEmail}>
                        Initiated by <span className="font-mono">{req.initiatorEmail}</span>
                      </p>
                      <p className="text-xs themed-text-muted mt-0.5">
                        State: <span className="themed-text">{stateLabel(req.state)}</span>
                        {req.sourceFolderId === null
                          ? <>{' · '}<span className="themed-text">connection-only</span></>
                          : <>{' · '}Permission: <span className="themed-text">{req.grantedPermission}</span></>}
                        {' · '}Updated {formatDate(req.stateChangedAt)}
                      </p>
                      <div className="mt-2 flex items-center gap-2">
                        {/* Confirming the recipient's org pick happens from
                            the notification bell now (click → Confirm popup).
                            Here it's status-only, plus the ability to cancel. */}
                        {needsConfirm && (
                          <span className="text-xs text-blue-400 italic">
                            Confirm from your notifications
                          </span>
                        )}
                        {/* Only where the caller may withdraw it — the outbox shows an
                            admin the whole org's, but each request is judged on its own
                            folder (#1075). An older API sends no flag: keep the button. */}
                        {req.canCancel !== false && (
                          <button
                            onClick={() => void handleCancel(req)}
                            className="px-2 py-1 text-xs themed-text-muted hover:text-red-400 flex items-center gap-1 transition-colors"
                          >
                            <X size={12} /> Cancel
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        </section>
      )}

      {/* ─── Add-to-network trigger (form opens in a modal) ───────── */}
      <button
        onClick={() => setAdding(true)}
        className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors"
      >
        <Plus size={12} /> Add to network
      </button>

      {adding && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-start justify-center px-4"
          onClick={closeAdd}
          onKeyDown={e => { if (e.key === 'Escape') closeAdd() }}
        >
          <div className="fixed inset-0 bg-black/50" />
          {/* Top edge pinned at 10vh so toggling Person/Organization only
              expands the modal downward — the top doesn't shift. */}
          <div
            className={`relative z-10 mt-[10vh] rounded-lg shadow-xl max-w-md w-full ${themeClass} themed-bg-secondary`}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 pt-4 pb-2">
              <h3 className="text-base font-semibold themed-text">Add to network</h3>
              <button
                onClick={closeAdd}
                className="p-1 themed-text-muted hover:themed-text transition-colors rounded"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="px-5 pb-5">
              <ConnectionForm
                mode="connection"
                onSubmit={handleSubmit}
                onCancel={closeAdd}
              />
              {sendWarning && (
                <div role="alertdialog" aria-labelledby="send-warning-title" data-testid="connect-send-warning"
                  className="mx-5 mb-4 rounded-md border border-amber-500/40 bg-amber-500/10 p-3">
                  <div id="send-warning-title" className="text-sm font-medium text-amber-300">Send another request?</div>
                  <p className="mt-1 text-xs themed-text whitespace-pre-line">{sendWarning.text}</p>
                  <div className="mt-2 flex gap-2">
                    <button type="button" autoFocus onClick={() => sendWarning.resolve(true)}
                      className="px-3 py-1 text-xs rounded bg-blue-600 hover:bg-blue-700 text-white">Send anyway</button>
                    <button type="button" onClick={() => sendWarning.resolve(false)}
                      className="px-3 py-1 text-xs rounded themed-text-muted hover:themed-text">Don't send</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* ─── Established connections ──────────────────────────────── */}
      {rows === null && !loadError && (
        <div className="flex items-center gap-2 themed-text-muted text-sm">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      )}

      {rows && rows.length === 0 && visibleOutgoing.length === 0 && (
        <div className="rounded-md border themed-border p-6 text-center">
          <Network size={24} className="mx-auto themed-icon-active opacity-50" />
          <p className="text-sm themed-text-muted mt-3">Your network is empty.</p>
          <p className="text-xs themed-text-muted mt-1">
            Add an organization above, or one will appear automatically the first time someone shares with you.
          </p>
        </div>
      )}

      {rows && rows.length > 0 && (
        <section className="space-y-3">
          <h3 className="text-sm themed-text font-medium flex items-center gap-2">
            <Network size={14} className="themed-icon" />
            People &amp; organizations
            <span className="text-xs themed-text-muted font-normal">({rows.length})</span>
          </h3>
          {/* Grid: single column on narrow, two columns on wider settings
              pane (matches the WhoHasAccessDialog right-pane tag grid). */}
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {rows.map(r => {
              const isEditing = editingContactFor === r.connectionId
              return (
                <li
                  key={r.connectionId}
                  /* Card matches the OutsiderTab's right-pane container
                     shape (rounded-2xl, themed-bg-tertiary, bordered)
                     so the two surfaces feel like siblings. `group`
                     enables the hover-revealed disconnect affordance. */
                  className="group relative themed-bg-tertiary rounded-2xl border themed-border hover:themed-border-secondary transition-colors overflow-hidden"
                >
                  {/* Header strip: avatar + identity + actions */}
                  <div className="flex items-center gap-3 px-4 py-3">
                    {/* Sky-toned avatar (sky = "your network" / connected;
                        emerald = "outsiders" on the per-folder tab). A
                        personal connection shows a person icon instead of
                        the building so the two are distinguishable. */}
                    <div className="w-10 h-10 rounded-full bg-sky-600 flex items-center justify-center shrink-0">
                      {r.org.type === 'personal'
                        ? <User size={16} className="text-white" />
                        : <Building2 size={16} className="text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="themed-text text-sm font-medium truncate flex items-center gap-1.5">
                        <span className="truncate">{r.peerIdentity?.displayName ?? r.org.name}</span>
                        {r.org.type === 'personal' && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-300 shrink-0 font-normal">Personal</span>
                        )}
                      </span>
                      {r.peerIdentity?.kind === 'personal' && r.peerIdentity.email && (
                        <p className="text-[11px] themed-text-muted mt-0.5 truncate" title={r.peerIdentity.email}>
                          {r.peerIdentity.email}
                        </p>
                      )}
                      <p className="text-[11px] themed-text-muted mt-0.5 truncate">
                        Connected {formatDate(r.establishedAt)}
                      </p>
                      {/* Fixed request-role audit — identical in both
                          tenants' views: who SENT the request and who
                          ACCEPTED it. Privacy view intact — audit emails,
                          not a directory. */}
                      <p className="text-[10px] themed-text-muted mt-0.5 truncate" title={`${r.initiatorEmail} · ${r.initiatorOrgName}`}>
                        Initiated by <span className="font-mono themed-text">{r.initiatorEmail}</span> · {r.initiatorOrgName}
                      </p>
                      {r.referredByEmail && (
                        <p className="text-[10px] themed-text-muted truncate" title={`${r.referredByEmail}${r.referredByOrgName ? ` · ${r.referredByOrgName}` : ''}`}>
                          Referred by <span className="font-mono themed-text">{r.referredByEmail}</span>{r.referredByOrgName ? ` · ${r.referredByOrgName}` : ''}
                        </p>
                      )}
                      <p className="text-[10px] themed-text-muted truncate" title={`${r.accepterEmail} · ${r.accepterOrgName}`}>
                        Accepted by <span className="font-mono themed-text">{r.accepterEmail}</span> · {r.accepterOrgName}
                      </p>
                    </div>
                    <button
                      onClick={() => void handleDisconnect(r)}
                      className="opacity-0 group-hover:opacity-100 focus:opacity-100 p-1.5 rounded-full themed-text-muted hover:text-red-400 hover:themed-bg-secondary transition-all shrink-0"
                      title={`Disconnect from ${r.org.name}`}
                      aria-label={`Disconnect from ${r.org.name}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>

                  {/* Peer-renamed warning band — soft surface, not a hard
                      revoke. Borders top/bottom anchor it between the
                      header and the contact editor. */}
                  {r.peerRenamed && (
                    <div className="px-4 py-2 bg-amber-500/10 border-t border-amber-500/20 text-[11px] flex items-start gap-2">
                      <AlertTriangle size={11} className="text-amber-400 shrink-0 mt-px" />
                      <span className="themed-text-muted leading-snug">
                        <span className="text-amber-300 font-medium">Identity changed.</span>{' '}
                        Connected as <span className="font-mono themed-text">{r.peerRenamed.auditEmail}</span>
                        {r.peerRenamed.currentEmail
                          ? <> — now <span className="font-mono themed-text">{r.peerRenamed.currentEmail}</span>.</>
                          : <> — no longer on record.</>}
                        {' '}Disconnect and re-add to re-confirm.
                      </span>
                    </div>
                  )}

                  {/* Inline default-contact editor */}
                  <div className="px-4 py-2.5 border-t themed-border-secondary flex items-center gap-2 text-[11px] min-h-[40px]">
                    <Mail size={11} className="themed-text-muted opacity-70 shrink-0" />
                    <span className="themed-text-muted shrink-0">Their default contact</span>
                    {isEditing ? (
                      <>
                        <input
                          type="email"
                          value={contactDraft}
                          onChange={e => setContactDraft(e.target.value)}
                          placeholder="(unset — uses their connecting email)"
                          autoFocus
                          onKeyDown={e => {
                            if (e.key === 'Enter') void handleSaveContact(r.connectionId)
                            if (e.key === 'Escape') setEditingContactFor(null)
                          }}
                          className="flex-1 themed-input border rounded px-2 py-0.5 text-[11px] outline-none focus:border-blue-500 min-w-0"
                        />
                        <button
                          onClick={() => void handleSaveContact(r.connectionId)}
                          className="text-blue-400 hover:text-blue-300 shrink-0"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingContactFor(null)}
                          className="themed-text-muted hover:themed-text shrink-0"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="ml-auto truncate text-right min-w-0">
                          <span className="themed-text font-mono">{r.theirDefaultContact}</span>
                          {!r.theirDefaultContactExplicit && (
                            <span className="themed-text-muted italic"> (auto)</span>
                          )}
                        </span>
                        <button
                          onClick={() => {
                            setContactDraft(r.theirDefaultContactExplicit ?? '')
                            setEditingContactFor(r.connectionId)
                          }}
                          className="text-blue-400 hover:text-blue-300 transition-colors shrink-0"
                        >
                          Edit
                        </button>
                      </>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        </section>
      )}
    </div>
  )
}

function stateLabel(s: OutgoingRequest['state']): string {
  switch (s) {
    case 'draft': return 'Draft'
    case 'pending_recipient': return 'Waiting for recipient to pick'
    case 'pending_initiator_confirm': return 'Awaiting your confirmation'
    // Completed/closed rows are rendered with their own label, not via this.
    case 'closed_rejected': return 'Closed'
  }
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return iso
  }
}

/** A request the outbox still shows but that is over: `already_connected` (completed by a sibling request) or
 *  `recipient_deleted` (#1213 — its recipient's organization or account was deleted). One card, two tones. */
function ResolvedRequestCard({ req, onDismiss }: {
  req: { requestId: string; recipientEmail: string; initiatorEmail: string; completed: 'already_connected' | 'recipient_deleted' | null }
  onDismiss: () => void
}) {
  const connected = req.completed === 'already_connected'
  return (
    <li
      className={connected ? 'p-3 rounded-md border border-green-500/30 bg-green-500/5' : 'p-3 rounded-md border themed-border themed-bg-tertiary'}
      data-testid={connected ? undefined : 'outgoing-recipient-deleted'}
    >
      <div className="flex items-start gap-3">
        {connected
          ? <Check size={16} className="text-green-400 mt-0.5 shrink-0" />
          : <X size={16} className="themed-text-muted mt-0.5 shrink-0" />}
        <div className="flex-1 min-w-0">
          <p className="text-sm themed-text">
            To <span className="font-mono">{req.recipientEmail}</span>
          </p>
          <p className="text-[11px] themed-text-muted mt-0.5 truncate" title={req.initiatorEmail}>
            Initiated by <span className="font-mono">{req.initiatorEmail}</span>
          </p>
          <p className={`text-xs mt-0.5 ${connected ? 'text-green-400' : 'themed-text-muted'}`}>
            {connected ? 'Completed · already connected' : 'Closed · the recipient\'s organization or account was deleted'}
          </p>
          <div className="mt-2">
            <button
              onClick={onDismiss}
              className="px-2 py-1 text-xs themed-text-muted hover:themed-text flex items-center gap-1 transition-colors"
            >
              <X size={12} /> Dismiss
            </button>
          </div>
        </div>
      </div>
    </li>
  )
}
